import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type React from "react";
import { useState } from "react";
import type { NskResponse } from "~/models/Api";
import type {
	CsvRequestParams,
	Encoding,
	Type
} from "~/models/CsvGenerationResult";
import { ENCODING_OPTIONS, TYPE_OPTIONS } from "~/models/CsvGenerationResult";
import type { DealSearchCondition } from "~/models/Deal";

export type DealsCsvExportModalProps = {
	handleClose: () => void;
	isOpen: boolean;
	requestCsvGenerate: (
		params: DealSearchCondition,
		csvType: Type,
		encoding: Encoding
	) => Promise<NskResponse<CsvRequestParams>>;
	searchParams: DealSearchCondition;
};

const DealsCsvExportModal: React.FC<DealsCsvExportModalProps> = ({
	searchParams,
	requestCsvGenerate,
	handleClose,
	isOpen
}) => {
	const { notify } = useNotification();
	const [csvType, setCsvType] = useState<Type>(TYPE_OPTIONS[0].value);
	const [encoding, setEncoding] = useState<Encoding>(ENCODING_OPTIONS[0].value);
	const [loading, setLoading] = useState(false);

	const handleDownload = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault();
		setLoading(true);
		const response = await requestCsvGenerate(searchParams, csvType, encoding);
		if (response.type === "success") {
			notify({
				content:
					"CSV出力リクエストを送信しました。作成後、メールで通知されます。",
				variant: "success"
			});
			handleClose();
		} else {
			const msg = response.message
				? `CSV出力リクエストに失敗しました。${response.message}`
				: "CSV出力リクエストに失敗しました";
			notify({ content: msg, variant: "error" });
		}
		setLoading(false);
	};

	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose} size="s">
			<ModalHeader title="CSV出力" />
			<ModalBody>
				<FormGroup variant="vertical">
					<FormControl chip="必須" label="出力種別">
						<InputSelect
							onChange={(e): void => {
								const val = e.target.value as Type;
								setCsvType(val);
							}}
							value={csvType}
							w="100%"
						>
							{TYPE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</InputSelect>
					</FormControl>
					<FormControl chip="必須" label="文字コード">
						<InputSelect
							onChange={(e): void => {
								const val = e.target.value as Encoding;
								setEncoding(val);
							}}
							value={encoding}
							w="100%"
						>
							{ENCODING_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</InputSelect>
					</FormControl>
					<Label color="subtle" size="s">
						※CSVの出力には時間がかかることがあります。作成後、メールで通知されます。
					</Label>
				</FormGroup>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						loading={loading}
						onClick={handleDownload}
						type="button"
						variant="primary"
					>
						出力
					</Button>
				}
			/>
		</Modalv2>
	);
};

export default DealsCsvExportModal;
